import { useState, useRef } from 'react';
import type { ArsColorCode, BarDiagramData } from '../../types/barDiagram';
import { arsColorStyles } from './barDiagramColors';
import { ColorPicker } from './ColorPicker';

interface Props {
  data: BarDiagramData;
  activePhaseIndex?: number;
  useRawOffset?: boolean;
  editable?: boolean;
  onCellColorChange?: (groupIdx: number, cellIdx: number, color: ArsColorCode) => void;
  onDurationChange?: (phaseIdx: number, duration: number) => void;
}

export function BarDiagramChart({
  data, activePhaseIndex, useRawOffset,
  editable, onCellColorChange, onDurationChange,
}: Props) {
  const { phases, groups, plans } = data;
  const totalDuration = phases.reduce((sum, p) => sum + p.duration, 0);
  const numPlans = plans.length;

  // ColorPicker state
  const [pickerTarget, setPickerTarget] = useState<{
    groupIdx: number; cellIdx: number; anchorRect: DOMRect;
  } | null>(null);

  // Duration editing state
  const [editingDuration, setEditingDuration] = useState<number | null>(null);
  const [durationValue, setDurationValue] = useState('');
  const durationInputRef = useRef<HTMLInputElement>(null);

  const handleCellClick = (groupIdx: number, cellIdx: number, e: React.MouseEvent<HTMLTableCellElement>) => {
    if (!editable) return;
    const rect = e.currentTarget.getBoundingClientRect();
    setPickerTarget({ groupIdx, cellIdx, anchorRect: rect });
  };

  const handleColorSelect = (color: ArsColorCode) => {
    if (pickerTarget && onCellColorChange) {
      onCellColorChange(pickerTarget.groupIdx, pickerTarget.cellIdx, color);
    }
    setPickerTarget(null);
  };

  const handleDurationClick = (phaseIdx: number) => {
    if (!editable) return;
    setEditingDuration(phaseIdx);
    setDurationValue(String(phases[phaseIdx].duration));
    setTimeout(() => durationInputRef.current?.select(), 0);
  };

  const commitDuration = () => {
    if (editingDuration === null) return;
    const val = parseInt(durationValue, 10);
    if (!isNaN(val) && val >= 1 && val <= 999 && onDurationChange) {
      onDurationChange(editingDuration, val);
    }
    setEditingDuration(null);
  };

  const cancelDuration = () => setEditingDuration(null);

  return (
    <div className="overflow-x-auto">
      <table
        style={{
          borderCollapse: 'collapse',
          width: '100%',
          fontFamily: 'Arial, Helvetica, sans-serif',
          fontSize: '13px',
          color: 'black',
        }}
      >
        <thead>
          {/* Header row: phase labels + plan durations */}
          <tr>
            <th
              style={{
                border: '1px solid black',
                padding: '4px 8px',
                backgroundColor: '#f5f5f5',
                minWidth: 50,
                verticalAlign: 'top',
                textAlign: 'center',
              }}
            >
              Dsf.
              {numPlans > 0 && (
                <>
                  <br /><br />
                  {plans.map((plan, i) => (
                    <span key={i}>
                      {useRawOffset ? plan.rawOffset : plan.offset}
                      {i < numPlans - 1 && <br />}
                    </span>
                  ))}
                </>
              )}
            </th>
            {phases.map((phase, i) => (
              <th
                key={i}
                style={{
                  border: activePhaseIndex === i ? '2px solid #3b82f6' : '1px solid black',
                  padding: '4px 8px',
                  backgroundColor: activePhaseIndex === i ? '#e0f2fe' : '#f5f5f5',
                  textAlign: 'center',
                  verticalAlign: 'top',
                  transition: 'background-color 0.15s, border-color 0.15s',
                }}
              >
                {phase.label}
                {numPlans > 0 && (
                  <>
                    <br /><br />
                    {plans.map((plan, pi) => (
                      <span key={pi}>
                        {plan.phaseDurations[i] ?? ''}
                        {pi < numPlans - 1 && <br />}
                      </span>
                    ))}
                  </>
                )}
              </th>
            ))}
          </tr>

          {/* T(seg) row */}
          <tr>
            <th
              style={{
                border: '1px solid black',
                padding: '4px 8px',
                backgroundColor: 'silver',
                textAlign: 'center',
              }}
            >
              T(seg)
            </th>
            {phases.map((phase, i) => (
              <td
                key={i}
                onClick={() => handleDurationClick(i)}
                style={{
                  border: activePhaseIndex === i ? '2px solid #3b82f6' : '1px solid black',
                  padding: '4px 8px',
                  backgroundColor: activePhaseIndex === i ? '#bfdbfe' : 'silver',
                  textAlign: 'center',
                  fontWeight: 'bold',
                  transition: 'background-color 0.15s, border-color 0.15s',
                  ...(editable ? { cursor: 'pointer' } : {}),
                }}
              >
                {editingDuration === i ? (
                  <input
                    ref={durationInputRef}
                    type="number"
                    min={1}
                    max={999}
                    value={durationValue}
                    onChange={e => setDurationValue(e.target.value)}
                    onBlur={commitDuration}
                    onKeyDown={e => {
                      if (e.key === 'Enter') commitDuration();
                      if (e.key === 'Escape') cancelDuration();
                    }}
                    autoFocus
                    style={{
                      width: 48,
                      textAlign: 'center',
                      fontWeight: 'bold',
                      fontSize: 13,
                      border: '1px solid #3b82f6',
                      borderRadius: 2,
                      outline: 'none',
                      padding: '1px 2px',
                    }}
                  />
                ) : (
                  phase.duration
                )}
              </td>
            ))}
          </tr>
        </thead>

        <tbody>
          {groups.map((group, gi) => {
            const allEmpty = group.cells.every(c => c === '-');
            return (
              <tr key={group.id}>
                <th
                  style={{
                    border: '1px solid black',
                    padding: '4px 8px',
                    backgroundColor: '#f5f5f5',
                    textAlign: 'center',
                    fontWeight: 'bold',
                  }}
                >
                  {group.label}
                </th>
                {group.cells.map((color, ci) => {
                  const phase = phases[ci];
                  const widthPercent = totalDuration > 0 && phase
                    ? (phase.duration / totalDuration) * 100
                    : 100 / phases.length;

                  return (
                    <td
                      key={ci}
                      onClick={(e) => handleCellClick(gi, ci, e)}
                      style={{
                        border: activePhaseIndex === ci ? '2px solid #3b82f6' : '1px solid black',
                        height: 30,
                        width: `${widthPercent}%`,
                        ...arsColorStyles[color],
                        ...(allEmpty ? { borderColor: '#ccc' } : {}),
                        transition: 'border-color 0.15s',
                        ...(editable ? { cursor: 'pointer' } : {}),
                      }}
                      title={`${group.label} - ${phase?.label ?? ''}: ${color}`}
                    />
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>

      {pickerTarget && (
        <ColorPicker
          currentColor={groups[pickerTarget.groupIdx]?.cells[pickerTarget.cellIdx] ?? 'p0'}
          onSelect={handleColorSelect}
          onClose={() => setPickerTarget(null)}
          anchorRect={pickerTarget.anchorRect}
        />
      )}
    </div>
  );
}
